import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CalendarCheckIcon, FileTextIcon, GaugeIcon, HourglassIcon, LightbulbIcon, PencilIcon, RefreshCwIcon, RepeatIcon, SearchXIcon, TriangleAlertIcon, UsersIcon } from 'lucide-react';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { TbdBadge } from '../components/ui/TbdBadge';
import { useHRStore } from '../hooks/useHRStore';
import type { Navigate, PolicyRule } from '../types/hr';
import type { IconComponent } from '../types/ui';
import { displayName } from '../utils/employees';
import { formatDate } from '../utils/format';
import { describePolicy } from '../utils/policy';
const SECTION_ICONS: Record<string, IconComponent> = {
  eligibility: HourglassIcon,
  accrual: RefreshCwIcon,
  caps: GaugeIcon,
  anniversary: CalendarCheckIcon,
  carryover: RepeatIcon
};
export function PolicyDetail({
  policyId,
  onNavigate
}: {
  policyId: string;
  onNavigate: Navigate;
}) {
  const {
    policies,
    employees
  } = useHRStore();
  const policy = policies.find(p => p.id === policyId);
  if (!policy) {
    return <EmptyState icon={SearchXIcon} title="Policy not found" action={<Button size="sm" onClick={() => onNavigate({
      page: 'timeoff',
      tab: 'policies'
    })} data-id="element-924" data-mp="2u6kbb4mvhfou">
            Back to policies
          </Button>} data-id="element-923" data-mp="2z55cb69y83ec" />;
  }
  const assigned = employees.filter(e => e.policyId === policy.id);
  return <div data-id="element-925" data-mp="3i14vbrky7wls">
      <button type="button" onClick={() => onNavigate({
      page: 'timeoff',
      tab: 'policies'
    })} className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted transition hover:text-navy" data-id="element-926" data-mp="3i0vg8z1fz9uv">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-927" data-mp="ef64qxwtfwc9" /> Time Off · Policies
      </button>

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-928" data-mp="9l1ykgvdjsc8">
        <div data-id="element-929" data-mp="sj53yimgxggs">
          <div className="flex flex-wrap items-center gap-2" data-id="element-930" data-mp="skomkfvlk3lq">
            <Badge tone="green" dot data-id="element-931" data-mp="xgn9dz1a1dk1">
              Active
            </Badge>
            <Badge tone="navy" data-id="element-932" data-mp="jqtizca4il87">{policy.type}-based</Badge>
            {policy.hasTbd && <TbdBadge data-id="element-933" data-mp="nrvhts04ov9n" />}
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-934" data-mp="nqtw8tlhkqdo">{policy.name}</h1>
          <p className="mt-1.5 text-sm text-muted" data-id="element-935" data-mp="9rcakn3hlt8j">Read-only summary · last updated {formatDate(policy.updated)} by Jordan Avery</p>
        </div>
        <Button variant="primary" icon={PencilIcon} onClick={() => onNavigate({
        page: 'policy-edit',
        policyId: policy.id
      })} data-id="element-936" data-mp="rfkqi5d2obw">
          Edit policy
        </Button>
      </div>

      {policy.hasTbd && <div className="mb-6 flex items-start gap-3 rounded-2xl bg-amber-50 px-5 py-4 ring-1 ring-inset ring-amber-200" data-id="element-937" data-mp="1v5q8ena7zcg3">
          <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-938" data-mp="1z7bsotp32t0b" />
          <div data-id="element-939" data-mp="3jotay1yawnrs">
            <p className="text-sm font-semibold text-amber-800" data-id="element-940" data-mp="3job9mqws7jd0">Some values are still to be decided</p>
            <p className="mt-0.5 text-sm text-amber-700" data-id="element-941" data-mp="3foalgejvrtsr">
              Fields marked <span className="font-semibold" data-id="element-942" data-mp="3temcsb8n5oiu">TBD</span> are placeholders. Employees stay enrolled; accruals post once values are set via “Edit policy”.
            </p>
          </div>
        </div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]" data-id="element-943" data-mp="77fqdl9brdp8">
        <div className="space-y-6" data-id="element-944" data-mp="28l94nr09x78">
          <Card title="Overview" icon={FileTextIcon} data-id="element-945" data-mp="29eaf9773uhz">
            <p className="text-sm leading-relaxed text-slate-700" data-id="element-946" data-mp="2axmsopqdleh">{policy.overview}</p>
            <p className="mt-4 rounded-xl bg-canvas px-4 py-3 text-sm text-slate-600" data-id="element-947" data-mp="g69tluh5v2br">
              <span className="font-semibold text-navy" data-id="element-948" data-mp="ba2lpw57fgr6">In plain English: </span>
              {describePolicy(policy.form).sentence}
            </p>
          </Card>

          {policy.sections.map(section => <Card key={section.id} title={section.title} icon={SECTION_ICONS[section.id] ?? FileTextIcon} data-id="element-949" data-mp="2d2qv6n8fz0fe">
              <dl className="divide-y divide-slate-100" data-id="element-950" data-mp="2fhnhuuz29dna">
                {section.rules.map(rule => <RuleRow key={rule.label} rule={rule} data-id="element-951" data-mp="2ofzizqxjzttz" />)}
              </dl>

              {section.id === 'anniversary' && policy.example && <div className="mt-5 rounded-2xl bg-flame/[0.06] p-5 ring-1 ring-inset ring-flame/20" data-id="element-952" data-mp="2tekbf46kd97v">
                  <p className="flex items-center gap-2 text-sm font-semibold text-navy" data-id="element-953" data-mp="1vt51n2w19akq">
                    <LightbulbIcon className="h-4 w-4 text-flame" data-id="element-954" data-mp="20qy3xku7tkfq" /> Worked example
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-955" data-mp="25o6pzwihi8m9">
                    <span className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-navy shadow-sm ring-1 ring-slate-200" data-id="element-956" data-mp="2ah9jk4n9hi2g">{policy.example.before}</span>
                    <ArrowRightIcon className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-957" data-mp="1hzngxerm1rt4" />
                    <span className="rounded-xl bg-flame px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-flame/30" data-id="element-958" data-mp="1age7jlj3tt42">{policy.example.added}</span>
                    <ArrowRightIcon className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-959" data-mp="15hukfhsryba4" />
                    <span className="rounded-xl bg-navy px-4 py-2.5 text-sm font-semibold text-white" data-id="element-960" data-mp="10h6rurowdmds">{policy.example.after}</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600" data-id="element-961" data-mp="rhjtyfvdmyph">{policy.example.caption}</p>
                </div>}
            </Card>)}
        </div>

        <div className="space-y-6 xl:sticky xl:top-24 xl:self-start" data-id="element-962" data-mp="moz1bho7irnj">
          <Card title="At a glance" data-id="element-963" data-mp="hs8lmvgbdy2n">
            <dl className="space-y-3" data-id="element-964" data-mp="csnngn8yxtqz">
              {policy.highlights.map(h => <div key={h.label} className="flex items-center justify-between rounded-xl bg-canvas px-4 py-3" data-id="element-965" data-mp="cqt0l3e4zrk9">
                  <dt className="text-sm text-muted" data-id="element-966" data-mp="3rxt7nzcwbfd">{h.label}</dt>
                  <dd className="text-sm font-semibold text-navy" data-id="element-967" data-mp="3sr3v01frl2s">{h.tbd ? <TbdBadge data-id="element-968" data-mp="3pbyqzcom9yil" /> : h.value}</dd>
                </div>)}
            </dl>
          </Card>

          <Card title="Assigned employees" subtitle={`${assigned.length} people`} icon={UsersIcon} bodyClassName="px-3 pb-3" data-id="element-969" data-mp="3ucdznqxyehu8">
            {assigned.length === 0 ? <p className="px-3 pb-2 text-sm text-muted" data-id="element-970" data-mp="3lad3yyidc3uo">No employees assigned yet.</p> : <ul className="max-h-[420px] space-y-0.5 overflow-y-auto" data-id="element-971" data-mp="cubcwyfdzz5l">
                {assigned.map(e => <li key={e.id} data-id="element-972" data-mp="gwok7r9s854h">
                    <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: e.id,
                tab: 'timeoff'
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-canvas/70" data-id="element-973" data-mp="gvn3p79zyqfd">
                      <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-974" data-mp="vruoa8pbhtv6" />
                      <span className="min-w-0" data-id="element-975" data-mp="10lqid02gmwl2">
                        <span className="block truncate text-sm font-medium text-navy" data-id="element-976" data-mp="19ypl1gmwo0nm">{displayName(e)}</span>
                        <span className="block truncate text-xs text-muted" data-id="element-977" data-mp="15yfheaml5mbx">{e.department}</span>
                      </span>
                    </button>
                  </li>)}
              </ul>}
          </Card>
        </div>
      </div>
    </div>;
}
function RuleRow({
  rule
}: {
  rule: PolicyRule;
}) {
  return <div className="grid grid-cols-1 gap-1 py-3.5 first:pt-0 last:pb-0 sm:grid-cols-[220px_minmax(0,1fr)] sm:gap-6" data-id="element-978" data-mp="r0n1vih1gxxq">
      <dt className="text-sm text-muted" data-id="element-979" data-mp="n14jatcowyy2">{rule.label}</dt>
      <dd data-id="element-980" data-mp="10zc8gf3hivyo">
        {rule.tbd ? <span className="inline-flex flex-wrap items-center gap-2" data-id="element-981" data-mp="10w6kj36le04j">
            <TbdBadge data-id="element-982" data-mp="vxvbguo4ftnd" />
            <span className="text-xs text-mutedlight" data-id="element-983" data-mp="w1j29m26m1ye">Placeholder — pending leadership decision</span>
          </span> : <span className="text-sm font-medium text-navy" data-id="element-984" data-mp="27f4szbnowbm1">{rule.value}</span>}
        {rule.note && <p className="mt-1 text-xs text-muted" data-id="element-985" data-mp="5pz8pg6swu0k">{rule.note}</p>}
      </dd>
    </div>;
}